/**
 * The width of the sidebar when it is collapsed.
 * Values 'small' and 'hidden' are deprecated and should not be used anymore.
 */
export type CollapsedWidth = '48px' | '0';
export type ExpandedWidth = '300px' | '400px';

type Listener = (state: [expanded: boolean, expandedWidth: ExpandedWidth, collapsedWidth: CollapsedWidth]) => void;

interface ClientDrawerState {
  expanded: boolean;
}

interface ClientSidebarOptions {
  expanded?: boolean;
  expandedWidth?: ExpandedWidth;
  collapsedWidth?: CollapsedWidth;
}

export class ClientSidebarManager {
  private static instance: ClientSidebarManager;
  private static STATE_KEY = 'CLIENT_DRAWER';
  private expanded: boolean = false;
  private expandedWidth: ExpandedWidth = '300px';
  private collapsedWidth: CollapsedWidth = '0';
  private listeners: Listener[] = [];
  private state: Map<string, ClientDrawerState> = new Map();
  private activeKey: string = 'default';

  private constructor() {
    const cached = sessionStorage.getItem(ClientSidebarManager.STATE_KEY);
    if (cached) {
      const cachedObj = JSON.parse(cached);

      Object.keys(cachedObj).forEach(key => {
        if (cachedObj[key]['expanded'] !== undefined) {
          this.state.set(key, { expanded: cachedObj[key]['expanded'] });
        }
      });
    }
  }

  /**
   * Sets the default state for the drawer of a certain key and marks it active.
   * Expanded is only applied if state was not taken from session storage.
   * @param key
   * @param options
   */
  public init(key: string, options: ClientSidebarOptions): void {
    if (!this.state.has(key)) {
      this.state.set(key, { expanded: options.expanded ?? false });
    }

    this.activeKey = key;
    this.expanded = this.state.get(key).expanded;
    this.expandedWidth = options.expandedWidth ?? '300px';
    this.collapsedWidth = options.collapsedWidth ?? '0';

    this.notify();
  }

  public setExpanded(value: boolean) {
    if (value === this.expanded) {
      return;
    }

    this.expanded = value;

    this.cache();
    this.notify();
  }

  public toggleExpanded() {
    this.expanded = !this.expanded;

    this.cache();
    this.notify();
  }

  /**
   * Hides the sidebar by setting it to closed with a width of '0'.
   * It also does NOT cache the state., so it can be used to temporarily hide the sidebar.
   */
  public hide() {
    if (!this.expanded && this.collapsedWidth === '0') {
      return;
    }

    this.collapsedWidth = '0';
    this.expanded = false;

    this.notify();
  }

  public subscribe(listener: Listener): void {
    this.listeners.push(listener);
    listener([this.expanded, this.expandedWidth, this.collapsedWidth]);
  }

  public unsubscribe(listener: Listener): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  private cache(): void {
    this.state.set(this.activeKey, { expanded: this.expanded });
    const cached = Object.fromEntries(this.state.entries());

    sessionStorage.setItem(ClientSidebarManager.STATE_KEY, JSON.stringify(cached));
  }

  private notify(): void {
    this.listeners.forEach(listener => listener([this.expanded, this.expandedWidth, this.collapsedWidth]));
  }

  public static getInstance(): ClientSidebarManager {
    if (!ClientSidebarManager.instance) {
      ClientSidebarManager.instance = new ClientSidebarManager();
    }
    return ClientSidebarManager.instance;
  }

  public static reset(): void {
    sessionStorage.removeItem(ClientSidebarManager.STATE_KEY);
    ClientSidebarManager.instance = new ClientSidebarManager();
  }
}
